"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import styles from "./layout.module.css";

type HeaderCartLinkProps = {
  href: string;
  label: string;
};

type CartItem = {
  cartId: string;
  name: string;
  image: string;
  unitPrice: number;
  accessoryPrice: number;
  quantity: number;
};

type CartSummary = {
  source?: string;
  message?: string;
  items?: CartItem[];
  count?: number;
  total?: number;
};

type RenderCartDraft = {
  recordId: string;
  fileName: string;
  createdAt: string;
  config?: {
    modelPart?: string;
    metal?: string;
    craft?: string;
    gemstone?: string;
    hdr?: string;
  };
};

type RenderCartBinding = {
  id: string;
  renderConfigRecordId: string;
  renderConfigSnapshotId: string;
  quantity: number;
  fileName: string;
  previewImagePath?: string | null;
};

const CART_DRAFT_KEY = "insparkle-render-cart-drafts";

function readRenderCartDrafts(): RenderCartDraft[] {
  try {
    const raw = window.localStorage.getItem(CART_DRAFT_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function describeDraft(draft: RenderCartDraft, isEnglish: boolean) {
  const config = draft.config || {};
  const parts = [config.modelPart, config.metal, config.craft, config.gemstone, config.hdr].filter(Boolean);
  if (parts.length === 0) {
    return isEnglish ? "Configuration pending" : "待继续配置";
  }
  return parts.join(" / ");
}

export function HeaderCartLink({ href, label }: Readonly<HeaderCartLinkProps>) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [cart, setCart] = useState<CartSummary | null>(null);
  const [drafts, setDrafts] = useState<RenderCartDraft[]>([]);
  const [renderBindings, setRenderBindings] = useState<RenderCartBinding[]>([]);
  const [loading, setLoading] = useState(false);
  const isEnglish = href.includes("lang=en-US");
  const text = isEnglish
    ? {
        product: "Product",
        price: "Unit Price",
        quantity: "Qty",
        total: "Total (excl. shipping)",
        viewCart: "View Cart",
        empty: "Your cart is empty.",
        unavailable: "Cart is unavailable.",
        draft: "Render draft"
      }
    : {
        product: "商品",
        price: "商品单价",
        quantity: "数量",
        total: "总计（不含运费）",
        viewCart: "去购物车查看",
        empty: "您的购物车还没有商品哦",
        unavailable: "暂时无法读取购物车。",
        draft: "渲染草稿"
      };

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      const localDrafts = readRenderCartDrafts();
      if (!cancelled) {
        setDrafts(localDrafts);
      }
      try {
        const [legacyResponse, renderResponse] = await Promise.all([
          fetch("/api/products/cart/count", { cache: "no-store" }),
          fetch("/api/render/persistence/cart", { cache: "no-store" }),
        ]);
        const data = (await legacyResponse.json()) as { count?: number };
        const bindings = renderResponse.ok ? ((await renderResponse.json()) as RenderCartBinding[]) : [];
        const legacyCount = Number.isFinite(Number(data.count)) ? Number(data.count) : 0;
        if (!cancelled) {
          setRenderBindings(bindings);
          setCount(legacyCount + bindings.length + localDrafts.length);
        }
      } catch {
        if (!cancelled) {
          setCount(localDrafts.length);
        }
      }
    }

    refresh();
    window.addEventListener("cart:refresh", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("cart:refresh", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  async function loadCart() {
    if (cart || loading) {
      return;
    }
    setLoading(true);
    const localDrafts = readRenderCartDrafts();
    setDrafts(localDrafts);
    try {
      const response = await fetch("/api/products/cart", { cache: "no-store" });
      const [data, bindingResponse] = await Promise.all([
        response.json() as Promise<CartSummary>,
        fetch("/api/render/persistence/cart", { cache: "no-store" }),
      ]);
      const bindings = bindingResponse.ok ? ((await bindingResponse.json()) as RenderCartBinding[]) : [];
      setCart(data);
      setRenderBindings(bindings);
      const legacyCount = Number.isFinite(Number(data.count)) ? Number(data.count) : data.items?.length || 0;
      setCount(legacyCount + bindings.length + localDrafts.length);
    } catch {
      setCart({ source: "unavailable", message: text.unavailable, items: [] });
      setCount(localDrafts.length);
    } finally {
      setLoading(false);
    }
  }

  const visibleItems = cart?.items?.slice(0, Math.max(0, 4 - drafts.length - renderBindings.length)) || [];
  const hasRows = visibleItems.length > 0 || drafts.length > 0 || renderBindings.length > 0;

  return (
    <span
      className={styles.cartWrap}
      onMouseEnter={() => {
        setOpen(true);
        loadCart();
      }}
      onMouseLeave={() => setOpen(false)}
    >
      <Link href={href} className={styles.cartLink} aria-label={label}>
        <span className={styles.cartIcon} aria-hidden="true" />
        <span className={styles.cartBadge}>{count}</span>
      </Link>
      {open ? (
        <span className={styles.cartPopover}>
          {loading ? (
            <span className={styles.cartEmpty}>...</span>
          ) : hasRows ? (
            <>
              <span className={styles.cartTableHead}>
                <span>{text.product}</span>
                <span>{text.price}</span>
                <span>{text.quantity}</span>
              </span>
              {visibleItems.map((item) => (
                <Link key={item.cartId} href={href} className={styles.cartRow}>
                  <span className={styles.cartProduct}>
                    <img src={item.image || "/images/noimg.jpg"} alt="" />
                    <span>{item.name}</span>
                  </span>
                  <span>¥{(item.unitPrice + item.accessoryPrice).toFixed(2)}</span>
                  <span>{item.quantity}</span>
                </Link>
              ))}
              {drafts.map((draft) => (
                <Link key={draft.recordId} href={href} className={styles.cartRow}>
                  <span className={styles.cartProduct}>
                    <span className={styles.cartDraftThumb}>FBX</span>
                    <span>
                      {draft.fileName}
                      <small>{describeDraft(draft, isEnglish)}</small>
                    </span>
                  </span>
                  <span>{text.draft}</span>
                  <span>1</span>
                </Link>
              ))}
              {renderBindings.map((binding) => (
                <Link key={binding.id} href={href} className={styles.cartRow}>
                  <span className={styles.cartProduct}>
                    {binding.previewImagePath ? <img src={binding.previewImagePath} alt="" /> : <span className={styles.cartDraftThumb}>FBX</span>}
                    <span>{binding.fileName}</span>
                  </span>
                  <span>{text.draft}</span>
                  <span>{binding.quantity}</span>
                </Link>
              ))}
              <span className={styles.cartTotal}>
                {text.total}: <strong>¥{Number(cart?.total || 0).toFixed(2)}</strong>
              </span>
              <Link href={href} className={styles.cartButton}>
                {text.viewCart}
              </Link>
            </>
          ) : (
            <span className={styles.cartEmpty}>{cart?.message || text.empty}</span>
          )}
        </span>
      ) : null}
    </span>
  );
}
